/**
 * AriaConversationContext — single source of truth for the ARIA conversation.
 *
 * Before this provider every ARIA surface kept its own `useState` conversation
 * (the full-page AIAssistant and the lateral AIPanel widget each had their own
 * `messages`/`conversationId`), so navigating away and back — or reloading —
 * started "from scratch" and the two surfaces never shared a thread.
 *
 * This provider lifts the conversation state + the streaming engine (useAIStream)
 * + persistence up to the Layout shell (mounted ABOVE the inner <Routes>), so it
 * survives route changes AND a hard reload (sessionStorage snapshot), and both
 * surfaces render the same conversation.
 *
 * Presentation concerns stay in the consumers and are injected PER CALL via the
 * `opts` arg of `sendMessage` (the live reasoning `tap`, `onTurnActivity`, the
 * canvas flag, the module, mentions/attachments, and a getter for the reasoning
 * snapshot). The store keeps the last opts so confirm/reject/HR-resend reuse the
 * same presentation context without the message cards having to thread it.
 */

import { createContext, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/utils/api';
import { safeStorage, safeSessionStorage } from '@/lib/safeStorage';
import { useAuth } from '@/lib/AuthContext';
import { track } from '@/lib/analytics';
import { captureLog } from '@/lib/errorReporter';
import useAIStream from './hooks/useAIStream';
import useAriaRun from './hooks/useAriaRun';
import { qk } from '@/lib/qk';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useHrAiOptIn } from '@/hooks/useHrAiOptIn';
import HrToolOptIn from './HrToolOptIn';
import { getFollowups } from './v2/followups';
import { ariaConversationScope } from './hooks/conversationScope';

const __LOG_CTX = { context: 'src/components/ai/AriaConversationContext' };

export const AriaConversationContext = createContext(null);

function greetingFor(date) {
  const hour = date.getHours();
  if (hour >= 6 && hour < 12) return 'Buenos días';
  if (hour >= 12 && hour < 20) return 'Buenas tardes';
  return 'Buenas noches';
}

const SNAPSHOT_VERSION = 1;
const convIdKey = (scopeKey) => `aria_conversationId_${encodeURIComponent(scopeKey)}`;
const snapshotKey = (scopeKey) => `aria_conv_snapshot_${encodeURIComponent(scopeKey)}`;
const LEGACY_PANEL_KEY = 'verix_aipanel_conversation_id';

const nowTime = () => new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

/**
 * Nombre de pila para el saludo, o cadena vacía.
 *
 * Antes caía a `user.email.split('@')[0]` cuando no había nombre, y saludaba
 * con «Hola usama.bz»: un identificador técnico puesto donde el usuario espera
 * el suyo, y además parte de su dirección en pantalla. La parte local de un
 * correo no es un nombre. Sin nombre se saluda sin nombre, que se lee bien.
 */
export function nombreDeSaludo(user) {
  const nombre = typeof user?.name === 'string' ? user.name.trim() : '';
  return nombre ? nombre.split(/\s+/)[0] : '';
}

export { ariaConversationScope } from './hooks/conversationScope';

export function AriaConversationProvider({ children }) {
  const { user } = useAuth();
  const scopeKey = ariaConversationScope(user);
  return <ScopedAriaConversationProvider key={scopeKey} scopeKey={scopeKey}>{children}</ScopedAriaConversationProvider>;
}

function ScopedAriaConversationProvider({ children, scopeKey }) {
  const { user } = useAuth();
  const userName = nombreDeSaludo(user);
  const dynamicGreeting = `${greetingFor(new Date())}${userName ? ' ' + userName : ''}`;

  const queryClient = useQueryClient();
  const companyId = user?.company_id;
  const identityReady = !!(companyId && (user?.id ?? user?.sub));
  const scopeAlive = useRef(true);
  const conversationEpochRef = useRef(0);
  const conversationEpoch = conversationEpochRef.current;

  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [pendingActions, setPendingActions] = useState({});         // msgId -> { action, status }
  const [pendingClarifications, setPendingClarifications] = useState({}); // msgId -> { question, options }
  const [actionResults, setActionResults] = useState({});           // msgId -> actionResult
  const [isLoading, setIsLoading] = useState(false);
  const [streamingId, setStreamingId] = useState(null);
  const [usageInfo, setUsageInfo] = useState(null);
  const [onboardingData, setOnboardingData] = useState(null);
  // Last running tool label — drives the compact widget's "Consultando X…" strip
  // (the full page derives richer per-message toolStatuses inside the stream).
  const [activeTool, setActiveTool] = useState(null);

  const aiStream = useAIStream();
  // E2 — transporte durable: la consulta se persiste y la ejecuta un worker sin
  // el techo de ~27 s; el cliente sólo la observa por cursor, así que cortar la
  // conexión deja de perder el turno. Contrato idéntico al de `useAIStream`
  // (mismos `params`, mismos callbacks), que es lo que permite elegir aquí sin
  // tocar nada más abajo.
  //
  // OFF por defecto en todos los entornos. `?run=1` lo fuerza y `?run=0` fuerza
  // el clásico — mismo patrón que el `?v=1` de invoices.form_v2, para poder
  // validar en dev sin arrastrar a todo el mundo y para tener rollback granular
  // por usuario cuando se encienda. Ver `aria.durable_run` en featureRegistry.
  const ariaRun = useAriaRun();
  const durableRunFlag = useFeatureFlag('aria.durable_run');
  const useDurableRun = useMemo(() => {
    const p = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('run')
      : null;
    if (p === '0') return false;
    return p === '1' || durableRunFlag.status === 'available';
  }, [durableRunFlag.status]);

  const abortTransport = useCallback(() => {
    // Se abortan los dos: si el flag cambia a mitad de turno (navegación con
    // `?run=`), el que quedó en vuelo es el del transporte anterior.
    aiStream.abort();
    ariaRun.abort();
  }, [aiStream, ariaRun]);

  const streamRafRef = useRef(null);
  const historyAbortRef = useRef(null);
  const historyRequestIdRef = useRef(0);
  // Double-click guard for write-action cards (approve/reject fire async state).
  const handledActionsRef = useRef(new Set());
  // Most recent presentation opts — reused by confirm/reject and HR re-send so
  // the live trace keeps feeding the surface that started the turn.
  const lastSendOptsRef = useRef({});
  // Mirror isLoading for the persistence effect (skip writes mid-stream).
  const streamingRef = useRef(false);
  // Gate the one-time network load (onboarding / history) behind first open.
  const historyLoadedRef = useRef(false);
  // Avoid persisting before the initial rehydration has run for this company.
  const hydratedRef = useRef(false);

  // HR AI opt-in (EU AI Act) — centralised here so both surfaces share one modal.
  const { modalOpen: hrModalOpen, openModal: openHrModal, closeModal: closeHrModal, refresh: refreshHrOptIn } = useHrAiOptIn();
  const pendingHrPromptRef = useRef(null);
  // H10 — al responder una aclaración ("21%", "no lo tengo") el backend
  // re-enruta esa respuesta corta a un dominio aleatorio. Recordamos el dominio
  // del último turno "normal" para que el badge y los follow-ups del turno de
  // respuesta (enviado con opts.answersClarification) lo hereden. FE-only.
  const lastRealDomainRef = useRef(null);

  // ── Rehydration (per company) ────────────────────────────────────────────
  // Snapshot first (instant, no network — restores messages + action cards),
  // then fall back to the persisted conversationId. Network load is deferred to
  // ensureLoaded() so we keep today's lazy behaviour (no onboarding fetch on
  // every page load).
  useEffect(() => {
    if (!identityReady) return;
    hydratedRef.current = false;
    historyLoadedRef.current = false;
    // Legacy single key cleanup (pre per-company scoping).
    try { safeStorage.removeItem('aria_conversationId'); } catch { /* noop */ }

    let restoredConvId = null;
    let restoredMessages = [];
    try {
      const raw = safeSessionStorage.getItem(snapshotKey(scopeKey));
      if (raw) {
        const snap = JSON.parse(raw);
        if (snap && snap.v === SNAPSHOT_VERSION && snap.scopeKey === scopeKey) {
          restoredConvId = snap.conversationId || null;
          restoredMessages = Array.isArray(snap.messages) ? snap.messages : [];
          setMessages(restoredMessages);
          setPendingActions(snap.pendingActions || {});
          setPendingClarifications(snap.pendingClarifications || {});
          setActionResults(snap.actionResults || {});
        }
      }
    } catch { /* corrupt snapshot — ignore */ }

    if (!restoredConvId) {
      try {
        restoredConvId = safeStorage.getItem(convIdKey(scopeKey));
        // Unscoped legacy identifiers cannot prove their user/permission owner.
        // Leave server history intact; a visible surface can request an authorized read.
      } catch { /* localStorage unavailable */ }
    }
    try { safeStorage.removeItem(LEGACY_PANEL_KEY); } catch { /* noop */ }

    setConversationId(restoredConvId || null);
    // If the snapshot already restored a thread, treat history as loaded so
    // ensureLoaded() doesn't overwrite it with a network fetch.
    if (restoredMessages.length > 0) historyLoadedRef.current = true;
    hydratedRef.current = true;
  }, [companyId, scopeKey, identityReady]);

  // Persist the conversationId (canonical per-company key).
  useEffect(() => {
    if (!identityReady || !hydratedRef.current) return;
    try {
      if (conversationId) safeStorage.setItem(convIdKey(scopeKey), conversationId);
      else safeStorage.removeItem(convIdKey(scopeKey));
    } catch { /* localStorage unavailable */ }
  }, [conversationId, companyId, scopeKey, identityReady]);

  // Persist a snapshot so the live thread survives reload. Skip mid-stream (the
  // message list churns ~60×/s); the post-stream render flushes the final state.
  useEffect(() => {
    if (!identityReady || !hydratedRef.current || streamingRef.current) return;
    try {
      const persistMessages = messages.filter((m) => !m.isThinking);
      const snap = {
        v: SNAPSHOT_VERSION,
        scopeKey,
        conversationId,
        messages: persistMessages,
        pendingActions,
        pendingClarifications,
        actionResults,
      };
      safeSessionStorage.setItem(snapshotKey(scopeKey), JSON.stringify(snap));
    } catch { /* quota / serialization — best effort */ }
    // `isLoading` is in the deps so the snapshot is (re)written once the stream
    // finishes: mid-stream writes are skipped (streamingRef guard) and the other
    // deps don't change on completion, so without this the final turn would never
    // be persisted.
  }, [messages, pendingActions, pendingClarifications, actionResults, conversationId, companyId, scopeKey, identityReady, isLoading]);

  const abortTransportRef = useRef(abortTransport);
  abortTransportRef.current = abortTransport;
  useLayoutEffect(() => {
    scopeAlive.current = true;
    return () => {
      scopeAlive.current = false;
      historyRequestIdRef.current += 1;
      historyAbortRef.current?.abort();
      if (streamRafRef.current) cancelAnimationFrame(streamRafRef.current);
      abortTransportRef.current();
    };
  }, []);

  // Invalidate before abort: cancellation can synchronously deliver transport callbacks.
  // This is local observation cleanup; durable server work/history is not deleted.
  const invalidateConversationWork = useCallback(() => {
    conversationEpochRef.current += 1;
    historyRequestIdRef.current += 1;
    historyAbortRef.current?.abort();
    historyAbortRef.current = null;
    if (streamRafRef.current) cancelAnimationFrame(streamRafRef.current);
    streamRafRef.current = null;
    abortTransport();
    setStreamingId(null);
    setActiveTool(null);
    setIsLoading(false);
    streamingRef.current = false;
    return conversationEpochRef.current;
  }, [abortTransport]);

  // ── History / onboarding load (network, lazy) ─────────────────────────────
  // Contador de petición: `selectConversation` puede llamar aquí dos veces
  // seguidas (cambio rápido de conversación) antes de que la primera resuelva.
  // Sin esto, la respuesta que llega TARDE pisa `messages` con el hilo de OTRA
  // conversación — cada rama que hace `setMessages` comprueba primero que
  // sigue siendo la petición más reciente.
  const readConversationHistory = useCallback(async (convId, moduleLabel, expectedEpoch) => {
    if (!identityReady || !scopeAlive.current || expectedEpoch !== conversationEpochRef.current) return;
    const requestId = ++historyRequestIdRef.current;
    historyAbortRef.current?.abort();
    const controller = new AbortController();
    historyAbortRef.current = controller;
    const isStale = () => !scopeAlive.current || expectedEpoch !== conversationEpochRef.current || historyRequestIdRef.current !== requestId;
    if (!convId) {
      // No previous conversation — fetch personalized onboarding.
      try {
        const obData = await apiFetch('/api/functions/invoke', {
          method: 'POST', signal: controller.signal, dedupe: false,
          body: JSON.stringify({ function: 'aiOrchestrator', params: { prompt: '__ONBOARDING__', stream: false } }),
        });
        if (isStale()) return;
        if (obData.onboarding) {
          setOnboardingData(obData.onboarding);
          const ob = obData.onboarding;
          // Saludo CORTO y personal — las capacidades ya las enseñan el orbe de
          // áreas y los chips de sugerencia; el bloque de bullets genérico solo
          // alargaba el primer mensaje de cada conversación.
          const welcomeText = `${ob.greeting} ${ob.roleContext}`.trim() || `¡${dynamicGreeting}! Soy ARIA. ¿En qué te ayudo hoy?`;
          setMessages([{ id: Date.now(), role: 'assistant', content: welcomeText, timestamp: nowTime() }]);
          return;
        }
      } catch (err) {
        captureLog('error', __LOG_CTX, '[Aria] Failed to load welcome:', err);
      }
      if (isStale()) return;
      setMessages([{
        id: Date.now(),
        role: 'assistant',
        content: `¡${dynamicGreeting}! Soy ARIA, tu asistente inteligente de VERIX. Puedo ayudarte con cualquier consulta sobre ${moduleLabel || 'tu empresa'}. Escríbeme o usa el micrófono para hablar.`,
        timestamp: nowTime(),
      }]);
      return;
    }

    try {
      const data = await apiFetch('/api/functions/invoke', {
        method: 'POST', signal: controller.signal, dedupe: false,
        body: JSON.stringify({ function: 'aiOrchestrator', params: { prompt: '__LOAD_HISTORY__', conversationId: convId, stream: false } }),
      });
      if (isStale()) return;
      if (data.history && data.history.length > 0) {
        setMessages(data.history.map((m, i) => ({
          id: Date.now() + i,
          role: m.role,
          content: m.content,
          ...(m.chart ? { chart: m.chart } : {}),
          timestamp: m.created_at ? new Date(m.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '',
        })));
      } else {
        setMessages([{ id: Date.now(), role: 'assistant', content: `¡${dynamicGreeting}! Soy ARIA, tu asistente inteligente de VERIX. Continuamos donde lo dejamos o pregunta algo nuevo.`, timestamp: nowTime() }]);
      }
    } catch {
      if (isStale()) return;
      setMessages([{ id: Date.now(), role: 'assistant', content: `¡${dynamicGreeting}! Soy ARIA, tu asistente inteligente de VERIX. ¿En qué puedo ayudarte?`, timestamp: nowTime() }]);
    }
  }, [dynamicGreeting, identityReady]);

  const loadConversationHistory = useCallback((convId = conversationId, moduleLabel = '') => (
    readConversationHistory(convId, moduleLabel, conversationEpoch)
  ), [readConversationHistory, conversationId, conversationEpoch]);

  // Called by a surface when it first opens. Loads onboarding/history once; if a
  // snapshot already rehydrated the thread, this is a no-op.
  const ensureLoaded = useCallback((moduleLabel = '') => {
    if (!identityReady || !scopeAlive.current || historyLoadedRef.current) return;
    historyLoadedRef.current = true;
    loadConversationHistory(conversationId, moduleLabel).catch((err) => {
      captureLog('error', __LOG_CTX, '[Aria] Error loading conversation-history:', err.message);
    });
  }, [conversationId, loadConversationHistory, identityReady]);

  // ── Send ──────────────────────────────────────────────────────────────────
  // Ported from AIAssistant.sendMessage. Presentation is injected via `opts`:
  //   { module, isCanvas, scanContext, mentions, attachments, tap, onTurnActivity,
  //     getReasoningSnapshot }
  const sendMessage = useCallback(async (text, opts = {}) => {
    // Reentrancia: un segundo Enter antes de que React repinte llega aquí
    // igual — el guard de InputBar es un swap de render (Enviar↔Cancelar), no
    // un `disabled`, y no cubre el teclado. Mismo patrón que ya usa el efecto
    // de `externalPrompt` en AIAssistant.jsx. Cierra el hueco en los TRES
    // chats: los tres comparten este `sendMessage`.
    if (!identityReady || !scopeAlive.current || conversationEpoch !== conversationEpochRef.current || isLoading) return { ok: false };
    const isCurrentTurn = () => scopeAlive.current && conversationEpoch === conversationEpochRef.current;
    const {
      module: moduleLabel = '',
      isCanvas = false,
      scanContext = null,
      mentions = [],
      attachments = [],
      scan_ids = [],
      import_files = [],
      word_docs = [],
      tap = null,
      onTurnActivity = null,
      getReasoningSnapshot = null,
      override = null,
    } = opts;
    lastSendOptsRef.current = opts;

    const hasAttachments = attachments.length > 0;
    const hasScanIds = Array.isArray(scan_ids) && scan_ids.length > 0;
    const hasImportFiles = Array.isArray(import_files) && import_files.length > 0;
    const hasWordDocs = Array.isArray(word_docs) && word_docs.length > 0;
    // Nada que mandar: se contesta `ok: false` para que el llamante no dé por
    // enviado lo que ni salió. Pasa de verdad — adjuntos que siguen subiendo o
    // que fallaron al subir no entran en `import_files` y dejan el envío vacío.
    if (!text.trim() && !hasAttachments && !hasScanIds && !hasImportFiles && !hasWordDocs) return { ok: false };
    const effectiveText = text.trim() || (hasScanIds
      ? 'He adjuntado estos documentos. Clasifícalos y dime qué hago con cada uno.'
      : hasImportFiles
        ? 'He adjuntado un fichero para importar. Detéctalo y dime qué se importaría.'
        : hasWordDocs
          ? 'He adjuntado un documento Word. Analízalo y dime qué hago con él.'
          : '¿Qué ves en esta imagen?');
    const sentAttachments = attachments.map(({ media_type, data }) => ({ media_type, data }));

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: effectiveText,
      attachmentsCount: sentAttachments.length,
      timestamp: nowTime(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);
    streamingRef.current = true;

    const thinkingMessage = { id: Date.now() + 1, role: 'assistant', content: '...', isThinking: true, timestamp: nowTime() };
    setMessages((prev) => [...prev, thinkingMessage]);

    // Backend caps conversationHistory at 30 (Zod schema → 400 "too_big" above
    // that). The store now rehydrates the full thread from the snapshot, which
    // can grow well past 30, so cap to the last 30 turns before sending.
    const conversationHistory = messages.filter((m) => !m.isThinking).slice(-30).map((m) => ({ role: m.role, content: m.content }));

    let fullText = '';
    let chart = null;
    let groundingWarning = null;
    let answerConfidence = null;
    const toolStatuses = new Map();
    const snapshotTools = () => Array.from(toolStatuses, ([name, s]) => ({ name, ...s }));
    let routing = null;
    let plan = [];
    const agentStatuses = new Map();
    const snapshotAgents = () => Array.from(agentStatuses, ([agent, s]) => ({ agent, ...s }));
    const turnDomains = [];
    const addTurnDomain = (d) => { if (d && !turnDomains.includes(d)) turnDomains.push(d); };
    const emitTurn = (status) => { if (isCurrentTurn()) onTurnActivity?.({ status, domains: [...turnDomains] }); };

    const setThinkingContent = (content, extraProps = {}) => {
      if (!isCurrentTurn()) return;
      setMessages((prev) => isCurrentTurn() ? prev.map((msg) => (msg.id === thinkingMessage.id ? { ...msg, content, isThinking: false, ...extraProps } : msg)) : prev);
    };

    let renderQueued = false;
    const flushStreamRender = () => {
      if (!isCurrentTurn()) return;
      renderQueued = false;
      streamRafRef.current = null;
      setThinkingContent(fullText, { toolStatuses: snapshotTools(), routing, plan, agentSteps: snapshotAgents() });
    };
    const scheduleStreamRender = () => {
      if (renderQueued) return;
      renderQueued = true;
      streamRafRef.current = requestAnimationFrame(flushStreamRender);
    };
    const cancelStreamRender = () => {
      if (streamRafRef.current) { cancelAnimationFrame(streamRafRef.current); streamRafRef.current = null; }
      renderQueued = false;
    };

    setStreamingId(thinkingMessage.id);
    setActiveTool(null);
    emitTurn('thinking');
    tap?.onBegin?.();

    let needRetryWithoutConv = false;
    // ¿Acabó el turno en error? Se devuelve al llamante para que pueda deshacer
    // lo que consumió al enviar. El panel borra los adjuntos AL ENVIAR, y sin
    // esta señal un fallo transitorio se los llevaba por delante: el usuario se
    // quedaba con el composer vacío sin saber ni qué había adjuntado.
    // Ojo: el opt-in de RRHH NO es un fallo — ese turno se reenvía solo con
    // `lastSendOptsRef`, adjuntos incluidos, así que restaurarlos los duplicaría.
    let turnFailed = false;
    const scopedTransport = useDurableRun ? ariaRun.run : aiStream.stream;
    const transport = (request) => scopedTransport({ ...request, callbacks: Object.fromEntries(
      Object.entries(request.callbacks).map(([name, callback]) => [name, (...args) => isCurrentTurn() ? callback(...args) : undefined]),
    ) });
    const runStream = (convId, allowRetry) => transport({
        message: effectiveText,
        params: {
          prompt: effectiveText,
          conversationHistory,
          conversationId: convId,
          module: moduleLabel,
          stream: true,
          ...(scanContext ? { scanContext } : {}),
          ...(mentions.length ? { mentions } : {}),
          ...(sentAttachments.length ? { attachments: sentAttachments } : {}),
          ...(hasScanIds ? { scan_ids } : {}),
          ...(hasImportFiles ? { import_files } : {}),
          ...(hasWordDocs ? { word_docs } : {}),
          ...(override?.reason ? { override: { reason: override.reason, ...(override.scope ? { scope: override.scope } : {}) } } : {}),
        },
        analyticsContext: { conversation_id: convId || null, module: moduleLabel },
        callbacks: {
          onText: (_delta, total) => { fullText = total; scheduleStreamRender(); tap?.onText?.(); },
          onRouting: (r) => {
            routing = r;
            addTurnDomain(r?.domain);
            (r?.crossDomains || []).forEach(addTurnDomain);
            emitTurn('working');
            scheduleStreamRender();
            tap?.onRouting?.(r);
          },
          onPlan: (steps) => { plan = Array.isArray(steps) ? steps : []; scheduleStreamRender(); tap?.onPlan?.(plan); },
          onThinkingText: (value) => { tap?.onThinkingText?.(value); },
          onAgentStart: ({ agent, domain }) => {
            agentStatuses.set(agent, { state: 'running', domain });
            addTurnDomain(domain);
            emitTurn('working');
            scheduleStreamRender();
            tap?.onAgentStart?.({ agent, domain });
          },
          // `servedFromSnapshot` marca el departamento que el snapshot ya cubría:
          // se pinta como resuelto sin que haya corrido un especialista, y no
          // costó una sola llamada al modelo. El backend lo emite justo para que
          // el panel pueda distinguirlo, y esta desestructuración lo tiraba.
          onAgentFinish: ({ agent, summary, error, tools, tier, costEur, citations, servedFromSnapshot }) => {
            const prev = agentStatuses.get(agent);
            agentStatuses.set(agent, { state: 'done', summary, error, domain: prev?.domain, tools, tier, costEur, citations, servedFromSnapshot });
            scheduleStreamRender();
            tap?.onAgentFinish?.({ agent, summary, error, servedFromSnapshot });
          },
          // E5 — el puente que faltaba. `useReasoningTrace` tiene `markFact` /
          // `markRevision`, `canvasTap` los cablea y `aiEventSink` reparte
          // `data.fact` / `data.revision`… pero ESTE objeto, que es el que
          // alimenta a los DOS transportes, no los reenviaba. El sink llamaba a
          // `onFact?.(…)` con `undefined` y no pasaba nada.
          //
          // Se construyeron tres capas y se saltó la del medio, que es justo la
          // que las une. Verificado en dev el 2026-08-03: un fan-out emitió 10
          // eventos `fact`, llegaron traducidos al frontal y el contador de la
          // sala siguió sin aparecer en 11 muestras seguidas.
          //
          // NO llevan estado propio en el contexto (a diferencia de
          // `agentStatuses`): son telemetría de la traza, así que se limitan a
          // pasar al tap. Sin `scheduleStreamRender`: `markFact` ya publica.
          onFact: (fact) => { tap?.onFact?.(fact); },
          onRevision: (revision) => { tap?.onRevision?.(revision); },
          // E1 — la confianza calculada del turno. A diferencia de fact/revision
          // sí lleva estado: se cuelga del mensaje en `onDone` para que la barra
          // de fuentes la muestre en "Cómo lo sé" junto a las salvedades.
          onAnswerConfidence: (ac) => { answerConfidence = ac; tap?.onAnswerConfidence?.(ac); },
          onToolThinking: (tool, _total, domain) => {
            toolStatuses.set(tool, { state: 'running', domain });
            setActiveTool(tool);
            addTurnDomain(domain);
            emitTurn('working');
            scheduleStreamRender();
            tap?.onToolThinking?.(tool, domain);
          },
          onToolFinish: ({ tool, preview, error, domain }) => {
            const prev = toolStatuses.get(tool);
            toolStatuses.set(tool, { state: 'done', preview, error, domain: domain ?? prev?.domain });
            scheduleStreamRender();
            tap?.onToolFinish?.({ tool, preview, error });
          },
          onChart: (chartData) => { chart = chartData; },
          onGroundingWarning: (gw) => { groundingWarning = gw; },
          onActionResult: (result) => { setActionResults((prev) => ({ ...prev, [thinkingMessage.id]: result })); },
          onConversationId: (id) => {
            setConversationId(id);
            // eslint-disable-next-line local/no-literal-querykey -- key standalone del rail ARIA; no hay entidad en qk.ts
            queryClient.invalidateQueries({ queryKey: ['aria-conversations', companyId] });
          },
          onUsage: (usage) => setUsageInfo(usage),
          onDone: ({ fullText: finalText, toolsUsed, agent, chart: finalChart, groundingWarning: finalGrounding, pendingActions: actions, pendingClarifications: clarifications, path }) => {
            cancelStreamRender();
            if (path === 'history') return;
            tap?.onDone?.();
            // eslint-disable-next-line local/no-literal-querykey -- key standalone del rail ARIA; no hay entidad en qk.ts
            queryClient.invalidateQueries({ queryKey: ['aria-conversations', companyId] });
            // El turno acaba de gastar créditos. Avisa QUIEN los gasta: los
            // medidores (`plan/AiCreditsMeter`) solo leen, y `usePlanUsage`
            // cachea 60 s — sin esto enseñarían el saldo de antes del turno justo
            // en el momento en que se mira. Que el aviso salga de aquí, y no del
            // medidor, es también lo que mantiene la frontera: `ai` puede
            // depender de la shell, nunca al revés (`nadie-depende-de-ai`).
            queryClient.invalidateQueries({ queryKey: qk.planUsage(companyId) });
            const reasoning = isCanvas && getReasoningSnapshot ? getReasoningSnapshot() : undefined;
            // H10 — dominio efectivo del turno. Si este envío RESPONDE una
            // aclaración (opts.answersClarification), hereda el dominio del
            // último turno normal en vez del re-enrutado de la respuesta corta.
            const rawTurnDomain = agent?.domain || routing?.domain || 'general';
            const answeringClarification = opts?.answersClarification === true;
            const turnDomain = (answeringClarification && lastRealDomainRef.current)
              ? lastRealDomainRef.current
              : rawTurnDomain;
            if (!answeringClarification && rawTurnDomain !== 'general') lastRealDomainRef.current = rawTurnDomain;
            // Remapea el snapshot de razonamiento (badge "Consultó X") al dominio
            // efectivo cuando se hereda, para no mostrar el dominio re-enrutado.
            const effReasoning = (answeringClarification && reasoning && Array.isArray(reasoning.steps))
              ? { ...reasoning, domain: turnDomain, steps: reasoning.steps.map((s) => ({ ...s, domain: turnDomain })) }
              : reasoning;
            addTurnDomain(turnDomain);
            (agent?.cross_domains || []).forEach(addTurnDomain);
            emitTurn('done');

            const tools = Array.isArray(toolsUsed) ? toolsUsed : [];
            const toolSuffix = tools.length > 0 ? '\n\n_Datos consultados: ' + tools.join(', ') + '_' : '';
            const sources = (() => {
              if (tools.length === 0) return undefined;
              const order = [];
              const byDomain = new Map();
              for (const name of tools) {
                const dom = toolStatuses.get(name)?.domain || routing?.domain || 'general';
                if (!byDomain.has(dom)) { byDomain.set(dom, []); order.push(dom); }
                byDomain.get(dom).push(name);
              }
              return order.map((domain) => ({ domain, tools: byDomain.get(domain) }));
            })();
            // `confidence` es la del ENRUTADO (qué tan seguro estaba el router de
            // a qué dominio iba la pregunta). `answerConfidence` es otra cosa y
            // mucho más útil: la confianza CALCULADA sobre la respuesta ya
            // producida. Conviven a propósito; no se sustituye una por otra.
            const sourceMeta = sources
              ? { confidence: routing?.confidence, modelTier: agent?.model_tier, crossDomains: agent?.cross_domains, answerConfidence: answerConfidence ?? undefined }
              : undefined;
            const followups = getFollowups(turnDomain, finalText || fullText || '');
            const reasoningTrace = snapshotAgents();
            setThinkingContent((finalText || fullText || 'Sin respuesta') + toolSuffix, {
              chart: finalChart ?? chart,
              // D-1 — aviso de grounding: se cuelga del mensaje del asistente de
              // este turno. Si el backend nunca lo emite (flag OFF), queda null
              // y MessageList no renderiza nada.
              groundingWarning: finalGrounding ?? groundingWarning ?? undefined,
              toolStatuses: [],
              routing: null,
              plan: [],
              agentSteps: [],
              reasoningTrace: reasoningTrace.length ? reasoningTrace : undefined,
              reasoning: effReasoning,
              sources,
              sourceMeta,
              followups,
            });
            if (actions && actions.length > 0) {
              const newActions = {};
              actions.forEach((a) => { newActions[thinkingMessage.id] = { action: a, status: 'pending' }; });
              setPendingActions((prev) => ({ ...prev, ...newActions }));
            }
            if (clarifications && clarifications.length > 0) {
              const last = clarifications[clarifications.length - 1];
              setPendingClarifications((prev) => ({ ...prev, [thinkingMessage.id]: last }));
            }
          },
          onError: ({ isTimeout, message, status, retryable }) => {
            // Stale persisted conversationId → clear it and retry once without it.
            if (status === 403 && convId && allowRetry) { needRetryWithoutConv = true; return; }
            turnFailed = true;
            cancelStreamRender();
            emitTurn('done');
            tap?.onError?.();
            const content = isTimeout
              ? 'La consulta ha tardado demasiado (timeout). Intenta con una pregunta más concreta o vuelve a intentarlo.'
              : (retryable && message) ? message : 'Lo siento, no pude procesar tu consulta. Inténtalo de nuevo.';
            setThinkingContent(content, retryable
              ? { isError: true, retryPrompt: text, toolStatuses: [], routing: null, plan: [], agentSteps: [] }
              : { isError: true, toolStatuses: [], routing: null, plan: [], agentSteps: [] });
          },
          onHrOptInRequired: () => {
            cancelStreamRender();
            emitTurn('done');
            setMessages((prev) => prev.filter((m) => m.id !== thinkingMessage.id && m.id !== userMessage.id));
            pendingHrPromptRef.current = text;
            openHrModal();
          },
        },
    });

    try {
      await runStream(conversationId, true);
      if (!isCurrentTurn()) return { ok: false };
      if (needRetryWithoutConv) {
        setConversationId(null);
        try { if (companyId) safeStorage.removeItem(convIdKey(scopeKey)); } catch { /* noop */ }
        await runStream(null, false);
      }
    } catch (err) {
      turnFailed = true;
      if (isCurrentTurn()) captureLog('error', __LOG_CTX, '[Aria] stream failed:', err?.message);
    } finally {
      if (isCurrentTurn()) {
        cancelStreamRender();
        setStreamingId(null);
        setActiveTool(null);
        setIsLoading(false);
        streamingRef.current = false;
      }
    }
    return { ok: isCurrentTurn() && !turnFailed };
  }, [conversationEpoch, identityReady, isLoading, messages, conversationId, companyId, scopeKey, aiStream, ariaRun, useDurableRun, queryClient, openHrModal]);

  const handleHrOptInAccepted = useCallback(async () => {
    if (!scopeAlive.current || conversationEpoch !== conversationEpochRef.current) return;
    await refreshHrOptIn();
    if (!scopeAlive.current || conversationEpoch !== conversationEpochRef.current) return;
    closeHrModal();
    const prompt = pendingHrPromptRef.current;
    pendingHrPromptRef.current = null;
    if (prompt) sendMessage(prompt, lastSendOptsRef.current);
  }, [conversationEpoch, refreshHrOptIn, closeHrModal, sendMessage]);

  // ── Action cards ────────────────────────────────────────────────────────
  const confirmAction = useCallback(async (msgId, editedMessage, override = null) => {
    if (!scopeAlive.current || conversationEpoch !== conversationEpochRef.current) return;
    if (handledActionsRef.current.has(msgId)) return;
    handledActionsRef.current.add(msgId);
    const action = pendingActions[msgId]?.action;
    setPendingActions((prev) => ({ ...prev, [msgId]: { ...prev[msgId], status: 'approved' } }));
    track('ai.action.approved', {
      conversation_id: conversationId || null,
      action_type: action?.type ?? 'unknown',
      edited: typeof editedMessage === 'string' && editedMessage.length > 0,
      forced: !!override?.reason,
    });
    // #6 flujo A — when the user forces an overridable invariant, carry
    // override:{reason, scope} on the confirm turn's request body (params). The
    // backend fixes approvedBy server-side and role-gates + audits it.
    const opts = override?.reason ? { ...lastSendOptsRef.current, override } : lastSendOptsRef.current;
    const result = await sendMessage(
      typeof editedMessage === 'string' && editedMessage.length > 0 ? editedMessage : 'Sí, confirma la operación.',
      opts,
    );
    // El guard de reentrancia de `sendMessage` puede devolver `{ok:false}` si un
    // turno anterior seguía en curso. Sin esto, la tarjeta quedaba marcada
    // "aprobada" sin que el turno de confirmación se hubiera enviado nunca — el
    // usuario creía haber aprobado y no había forma de reintentar.
    if (result && result.ok === false && scopeAlive.current && conversationEpoch === conversationEpochRef.current) {
      handledActionsRef.current.delete(msgId);
      setPendingActions((prev) => ({ ...prev, [msgId]: { ...prev[msgId], status: 'pending' } }));
    }
  }, [conversationEpoch, pendingActions, conversationId, sendMessage]);

  // Rechazar NO abre turno de chat, a diferencia de `confirmAction`: cancelar no
  // necesita que el modelo opine, y gastar un turno (y su latencia) por un "no" es
  // caro para nada. Pero sí tiene que CERRARSE contra el servidor y CONTESTARSE en
  // el hilo — antes no hacía ni lo uno ni lo otro:
  //   · el POST iba sin `await` y con el error tragado, así que una cancelación
  //     fallida era indistinguible de una buena;
  //   · la acción seguía viva en `ai_messages.tool_calls_json` con
  //     `pendingAction: true`, y `getLastPendingAction` la volvía a encontrar: un
  //     "sí" posterior en la misma conversación podía reanclar la escritura que el
  //     usuario acababa de descartar;
  //   · y el usuario se quedaba mirando un mensaje suyo sin respuesta, sin saber si
  //     se había ejecutado algo.
  const rejectAction = useCallback(async (msgId) => {
    if (!scopeAlive.current || conversationEpoch !== conversationEpochRef.current) return;
    if (handledActionsRef.current.has(msgId)) return;
    handledActionsRef.current.add(msgId);
    const action = pendingActions[msgId]?.action;
    setPendingActions((prev) => ({ ...prev, [msgId]: { ...prev[msgId], status: 'rejected' } }));
    track('ai.action.rejected', { conversation_id: conversationId || null, action_type: action?.type ?? 'unknown' });
    setMessages((prev) => [...prev, { id: Date.now(), role: 'user', content: 'Cancelar, no ejecutar la operación.', timestamp: nowTime() }]);

    let cerrada = false;
    try {
      const res = await apiFetch('/api/ai/cancel-action', {
        method: 'POST',
        body: JSON.stringify({ conversationId: conversationId || undefined, messageId: String(msgId), reason: 'user_rejected' }),
      });
      // `nothing_pending` también es un cierre correcto: no había nada que anular
      // (p. ej. el turno no llegó a persistirse). Lo que no vale es `no_conversation`.
      cerrada = res?.outcome === 'cancelled' || res?.outcome === 'nothing_pending';
    } catch {
      cerrada = false;
    }

    if (!scopeAlive.current || conversationEpoch !== conversationEpochRef.current) return;
    setMessages((prev) => [...prev, {
      id: Date.now() + 1,
      role: 'assistant',
      content: cerrada
        ? 'Operación cancelada. No he ejecutado nada.'
        : 'He descartado la operación aquí, pero no he podido confirmarlo con el servidor. Revísalo antes de volver a pedírmelo.',
      timestamp: nowTime(),
      ...(cerrada ? {} : { isError: true }),
    }]);
  }, [conversationEpoch, pendingActions, conversationId]);

  // ── Conversation lifecycle ────────────────────────────────────────────────
  const startNewConversation = useCallback(() => {
    if (!identityReady || !scopeAlive.current) return;
    invalidateConversationWork();
    historyLoadedRef.current = true;
    pendingHrPromptRef.current = null;
    lastRealDomainRef.current = null;
    handledActionsRef.current.clear();
    closeHrModal();
    setUsageInfo(null);
    setOnboardingData(null);
    setConversationId(null);
    lastSendOptsRef.current?.onTurnActivity?.({ status: 'idle', domains: [] });
    lastSendOptsRef.current = {};
    try {
      safeStorage.removeItem('aria_conversationId');
      if (companyId) safeStorage.removeItem(convIdKey(scopeKey));
    } catch { /* ignore */ }
    setPendingActions({});
    setPendingClarifications({});
    setActionResults({});
    setMessages([{ id: Date.now(), role: 'assistant', content: 'Nueva conversación iniciada. ¿En qué puedo ayudarte?', timestamp: nowTime() }]);
  }, [identityReady, invalidateConversationWork, closeHrModal, companyId, scopeKey]);

  const selectConversation = useCallback((target) => {
    if (!identityReady || !scopeAlive.current || !target || target === conversationId) return;
    const nextEpoch = invalidateConversationWork();
    pendingHrPromptRef.current = null;
    lastRealDomainRef.current = null;
    handledActionsRef.current.clear();
    closeHrModal();
    setUsageInfo(null);
    setOnboardingData(null);
    setConversationId(target);
    setPendingActions({});
    setPendingClarifications({});
    setActionResults({});
    setMessages([]);
    historyLoadedRef.current = true;
    lastSendOptsRef.current?.onTurnActivity?.({ status: 'idle', domains: [] });
    lastSendOptsRef.current = {};
    readConversationHistory(target, '', nextEpoch).catch((err) => {
      captureLog('error', __LOG_CTX, '[Aria] Error switching conversation:', err.message);
    });
  }, [identityReady, conversationId, invalidateConversationWork, closeHrModal, readConversationHistory]);

  const cancelRequest = useCallback(() => {
    invalidateConversationWork();
    setMessages((prev) => prev.map((msg) => (msg.isThinking ? { ...msg, content: '⏹️ Cancelado por el usuario.', isThinking: false } : msg)));
    setIsLoading(false);
    streamingRef.current = false;
  }, [invalidateConversationWork]);

  const value = useMemo(() => ({
    scopeKey,
    messages,
    conversationId,
    pendingActions,
    pendingClarifications,
    actionResults,
    isLoading,
    streamingId,
    usageInfo,
    onboardingData,
    activeTool,
    sendMessage,
    confirmAction,
    rejectAction,
    startNewConversation,
    selectConversation,
    loadConversationHistory,
    ensureLoaded,
    cancelRequest,
    abort: abortTransport,
  }), [
    scopeKey, messages, conversationId, pendingActions, pendingClarifications, actionResults, isLoading,
    streamingId, usageInfo, onboardingData, activeTool, sendMessage, confirmAction, rejectAction,
    startNewConversation, selectConversation, loadConversationHistory, ensureLoaded, cancelRequest, abortTransport,
  ]);

  return (
    <AriaConversationContext.Provider value={value}>
      {children}
      <HrToolOptIn open={hrModalOpen} onClose={closeHrModal} onAccepted={handleHrOptInAccepted} />
    </AriaConversationContext.Provider>
  );
}

AriaConversationProvider.propTypes = {
  children: PropTypes.node,
};

ScopedAriaConversationProvider.propTypes = { children: PropTypes.node, scopeKey: PropTypes.string.isRequired };
